import { deserialize, serialize } from './serialization';
import { FieldTypes } from './types';

const negatedFilterMap = {
  containKeyValue: 'doNotContainKeyValue',
  contains: 'doesNotContain',
  equals: 'doesNotEqual',
  isInIpRange: 'isNotInIpRange',
  matchesRegex: 'doesNotMatchRegex',
  matchesWildcard: 'doesNotMatchWildcard',
};
const toNegatedFilterMap = {
  doesNotContain: 'contains',
  doNotContainKeyValue: 'containKeyValue',
  doesNotEqual: 'equals',
  doesNotMatchRegex: 'matchesRegex',
  doesNotMatchWildcard: 'matchesWildcard',
  isNotInIpRange: 'isInIpRange',
};

/**
 * Pre-process the filters before sending to the serializer.
 */
export function serializeQuery(
  filters: any[],
  preProcessFilter?: (filter: any) => void
) {
  filters.forEach((filter: any) => {
    if (filter?.operator && toNegatedFilterMap[filter.operator]) {
      filter.operator = toNegatedFilterMap[filter.operator];
      filter.negated = true;
    }

    if (preProcessFilter) {
      preProcessFilter(filter);
    }
  });

  return serialize(filters);
}

// Checks if there is at least one key from negatedFilterMap
// and toNegatedFilterMap for the respective filter type.
function hasPairing(filter: any, fields: FieldTypes[] | undefined): boolean {
  const field = fields?.find((f) => f.prop === filter?.prop);
  const fieldOperator = field?.operators?.find(
    (o) => o.type === filter?.operator
  );

  // If it couldn't find the field's operator
  // or the field's operator isn't in negatedFilterMap and toNegatedFilterMap
  if (
    fieldOperator === undefined ||
    (!negatedFilterMap.hasOwnProperty(fieldOperator.type) &&
      !toNegatedFilterMap.hasOwnProperty(fieldOperator.type))
  ) {
    return false;
  } else {
    const inverseFieldOperator = field?.operators?.find(
      (o) =>
        o.type === negatedFilterMap[fieldOperator.type] ||
        o.type === toNegatedFilterMap[fieldOperator.type]
    );

    return inverseFieldOperator !== undefined;
  }
}

/**
 * Post-process the DSL string before returning the filters.
 */
export function deserializeQuery(
  dslString?: string,
  postProcessFilter?: (filter: any) => void,
  fields?: FieldTypes[]
) {
  if (!dslString) {
    return [];
  }

  const filters = deserialize(dslString);

  filters.forEach((filter: any) => {
    // Checks whether filter.operator and its inverse exist in
    // negatedFilterMap and toNegatedFilterMap, or vice versa.
    const hasInverse = hasPairing(filter, fields);

    if (filter?.negated && negatedFilterMap[filter.operator] && hasInverse) {
      filter.operator = negatedFilterMap[filter.operator];
      filter.negated = false;
    }

    if (filter.start && filter.end) {
      if (filter.start.modifiers && filter.start.modifiers.length > 0) {
        filter.operator = 'past';
      } else if (filter.end.modifiers && filter.end.modifiers.length > 0) {
        filter.operator = 'next';
      } else {
        filter.operator = 'between';
      }
    }

    if (filter.operator === 'keyword') {
      filter.value = filter.value.replace(/["|/\\]+/g, '');
    }

    if (postProcessFilter) {
      postProcessFilter(filter);
    }
  });

  return filters;
}
